/**
 * 貨幣換算工具
 */

// 匯率快取（從系統設定讀取，預設為 32.5）
let cachedExchangeRate = parseFloat(process.env.VUE_APP_USD_TO_TWD_RATE || '32.5');
let rateInitialized = false;

/**
 * 初始化匯率（從系統設定讀取）
 * @returns {Promise<number>} 匯率值
 */
export async function initExchangeRate() {
  if (rateInitialized) {
    return cachedExchangeRate;
  }

  try {
    // 動態導入 API client（避免循環依賴）
    const { default: apiClient } = await import('@/api/axios.js');
    const response = await apiClient.get('/system/settings/usd_to_twd_rate');
    
    if (response && response.success && response.data) {
      cachedExchangeRate = parseFloat(response.data.value || 32.5);
      rateInitialized = true;
      return cachedExchangeRate;
    }
  } catch (err) {
    console.warn('無法取得匯率設定，使用預設值:', cachedExchangeRate, err);
    // 如果設定不存在或請求失敗，使用預設值
    rateInitialized = true;
  }
  
  return cachedExchangeRate;
}

/**
 * 取得當前匯率（同步版本，如果尚未初始化則使用預設值）
 * @returns {number} 匯率值
 */
export function getExchangeRate() {
  return cachedExchangeRate;
}

/**
 * 設定匯率（用於更新快取）
 * @param {number} rate - 新的匯率值
 */
export function setExchangeRate(rate) {
  cachedExchangeRate = parseFloat(rate || 32.5);
  rateInitialized = true;
}

/**
 * 將 USD 換算為 TWD（台幣）
 * @param {number} usdAmount - USD 金額
 * @param {number} decimals - 小數位數（預設 2）
 * @returns {number} TWD 金額
 */
export function usdToTwd(usdAmount, decimals = 2) {
  if (!usdAmount || isNaN(usdAmount)) {
    return 0;
  }
  const twdAmount = parseFloat(usdAmount) * cachedExchangeRate;
  return parseFloat(twdAmount.toFixed(decimals));
}

/**
 * 格式化台幣金額顯示
 * @param {number} usdAmount - USD 金額
 * @param {number} decimals - 小數位數（預設 2）
 * @returns {string} 格式化後的台幣金額字串
 */
export function formatTwd(usdAmount, decimals = 2) {
  const twdAmount = usdToTwd(usdAmount, decimals);
  return `NT$ ${twdAmount.toLocaleString('zh-TW', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/**
 * 格式化已儲存的台幣金額顯示（不需要換算）
 * @param {number} twdAmount - 已儲存的台幣金額
 * @returns {string} 格式化後的台幣金額字串
 */
export function formatTwdFromStored(twdAmount) {
  if (!twdAmount || isNaN(twdAmount)) {
    return 'NT$ 0.00';
  }
  const amount = parseFloat(twdAmount);
  return `NT$ ${amount.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * 格式化 USD 金額顯示
 * @param {number} usdAmount - USD 金額
 * @param {number} decimals - 小數位數（預設 6）
 * @returns {string} 格式化後的 USD 金額字串
 */
export function formatUsd(usdAmount, decimals = 6) {
  if (!usdAmount || isNaN(usdAmount)) {
    return '$0.000000';
  }
  return `$${parseFloat(usdAmount).toFixed(decimals)} USD`;
}
